import { CheckCircle2, XCircle } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton.js';
import { cn } from '@/lib/cn.js';

/**
 * Petites pièces de mise en page partagées par les onglets de Paramètres, pour
 * que les cinq onglets gardent le même rythme (`README-shell.md`, « Gabarits »).
 */

export interface ConnectionTestMessageProps {
  ok: boolean;
  message: string;
  detail?: string | undefined;
  durationMs?: number | undefined;
  className?: string | undefined;
}

/** Résultat inline d'un bouton « Tester » : vert/rouge, durée en chiffres tabulaires. */
export function ConnectionTestMessage({
  ok,
  message,
  detail,
  durationMs,
  className,
}: ConnectionTestMessageProps): JSX.Element {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <p
      className={cn(
        'inline-flex min-w-0 items-start gap-1.5 text-sm',
        ok ? 'text-status-green-fg' : 'text-status-red-fg',
        className,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 break-words">
        {message}
        {detail ? ` — ${detail}` : null}
        {durationMs === undefined ? null : <span className="tabular-nums"> ({durationMs} ms)</span>}
      </span>
    </p>
  );
}

/**
 * Tableau posé dans un `PageSection` panneau : il occupe toute la largeur du
 * panneau (marges internes annulées) plutôt que d'ajouter un cadre imbriqué.
 * `atEnd` : dernier élément du panneau, collé au bord bas.
 */
export function SectionTable({
  children,
  atEnd = false,
}: {
  children: ReactNode;
  atEnd?: boolean;
}): JSX.Element {
  return (
    <div
      className={cn(
        '-mx-5 min-w-0 border-t border-border',
        atEnd ? '-mb-5 overflow-hidden rounded-b-lg' : 'border-b',
      )}
    >
      {children}
    </div>
  );
}

/** Squelette d'une section de réglages : même cadre que `PageSection`, titre, aide et champs. */
export function SettingsSectionSkeleton({ fields = 4 }: { fields?: number }): JSX.Element {
  return (
    <div aria-hidden="true" className="rounded-lg border border-border bg-card">
      <div className="flex flex-col gap-2 px-5 pt-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-72 max-w-full" />
      </div>
      <div className="grid gap-4 px-5 pb-5 pt-4 sm:grid-cols-2">
        {Array.from({ length: fields }, (_, index) => (
          <div key={`field-${String(index)}`} className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}
